import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, useInView } from "framer-motion";
import { 
  ArrowRight, Star, ChevronRight, Users, Calendar, FileText, BarChart3, Shield, Zap,
  CheckCircle, Building2, Stethoscope, HeartPulse
} from "lucide-react";
import api from "@/lib/api";

// Animation variants
const fadeUp = {
  hidden: { opacity: 0, y: 40 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.8, ease: [0.4, 0, 0.2, 1] } }
};

const staggerContainer = {
  hidden: { opacity: 0 },
  visible: { opacity: 1, transition: { staggerChildren: 0.1, delayChildren: 0.2 } }
};

// Animated Section Wrapper
const AnimatedSection = ({ children, className = "", delay = 0 }) => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: "-100px" });
  
  return (
    <motion.div
      ref={ref}
      initial="hidden"
      animate={isInView ? "visible" : "hidden"}
      variants={{
        hidden: { opacity: 0, y: 50 },
        visible: { opacity: 1, y: 0, transition: { duration: 0.8, delay, ease: [0.4, 0, 0.2, 1] } }
      }}
      className={className}
    >
      {children}
    </motion.div>
  );
};

// Hero Section
const HeroSection = () => {
  const { scrollY } = useScroll();
  const y = useTransform(scrollY, [0, 500], [0, 150]);
  const opacity = useTransform(scrollY, [0, 300], [1, 0]);

  return (
    <section className="hero-section relative" data-testid="hero-section">
      <motion.div className="absolute inset-0 z-0" style={{ y }}>
        <img
          src="https://images.unsplash.com/photo-1497366216548-37526070297c?w=1920"
          alt="Modern Office"
          className="w-full h-full object-cover"
        />
      </motion.div>
      <div className="hero-overlay z-10" />

      <div className="relative z-20 w-full max-w-[1400px] mx-auto px-6 md:px-12 pt-32 md:pt-40 pb-20">
        <div className="max-w-3xl">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.2 }}>
            <div className="flex items-center gap-3 mb-8">
              <div className="gold-divider" />
              <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#4A4A4A]">Healthcare Software Solutions</span>
            </div>
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.4 }}
            className="font-display text-5xl md:text-7xl lg:text-8xl text-[#1A1A1A] mb-8 leading-[1.05]"
          >
            Streamline
            <br />
            <span className="italic text-[#1B4D3E]">Healthcare</span>
            <br />
            Operations
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.6 }}
            className="text-lg md:text-xl text-[#4A4A4A] font-light leading-relaxed max-w-xl mb-12"
          >
            Powerful client management software built for healthcare companies. 
            From patient scheduling to billing—we help you run a more efficient practice.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="flex flex-col sm:flex-row gap-4"
          >
            <Link to="/book" className="btn-gold magnetic-btn" data-testid="hero-cta">
              Request Demo
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/services" className="btn-secondary magnetic-btn">
              View Solutions
            </Link>
          </motion.div>
        </div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1 }}
          className="absolute bottom-20 right-12 hidden lg:flex items-center gap-16"
          style={{ opacity }}
        >
          <div className="text-right">
            <div className="stat-number">500+</div>
            <div className="stat-label">Healthcare Clients</div>
          </div>
          <div className="w-px h-16 bg-[#D4AF37]/30" />
          <div className="text-right">
            <div className="stat-number">99.9%</div>
            <div className="stat-label">Uptime</div>
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 1.2 }}
          className="scroll-indicator hidden md:block"
          style={{ opacity }}
        >
          <span className="text-xs tracking-[0.2em] uppercase text-[#888]">Scroll</span>
        </motion.div>
      </div>
    </section>
  );
};

// Features/Services Section
const features = [
  {
    icon: Users,
    title: "Client Management",
    description: "Centralized patient records, contact management, and communication tools all in one place.",
  },
  {
    icon: Calendar,
    title: "Smart Scheduling",
    description: "Automated appointment booking, reminders, and calendar sync to reduce no-shows.",
  },
  {
    icon: FileText,
    title: "Documentation",
    description: "Digital forms, e-signatures, and secure document storage for paperless operations.",
  },
  {
    icon: BarChart3,
    title: "Analytics & Reports",
    description: "Real-time insights into practice performance, revenue, and patient trends.",
  },
  {
    icon: Shield,
    title: "HIPAA Compliant",
    description: "Enterprise-grade security with full HIPAA compliance to protect patient data.",
  },
  {
    icon: Zap,
    title: "Integrations",
    description: "Connect with EHR systems, billing software, labs, and telehealth platforms.",
  }
];

const FeaturesSection = () => {
  return (
    <section id="services" className="py-32 md:py-40 bg-[#F9F9F7]" data-testid="services-section">
      <div className="max-w-[1400px] mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="gold-divider" />
            <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">Platform Features</span>
            <div className="gold-divider" />
          </div>
          <h2 className="font-display text-4xl md:text-6xl text-[#1A1A1A] mb-6">Everything You Need</h2>
          <p className="text-lg text-[#4A4A4A] font-light max-w-2xl mx-auto">
            A complete suite of tools designed specifically for healthcare organizations of all sizes.
          </p>
        </AnimatedSection>

        <motion.div
          variants={staggerContainer}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true, margin: "-100px" }}
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {features.map((feature, index) => (
            <motion.div 
              key={feature.title} 
              variants={fadeUp}
              className="bg-white rounded-3xl p-8 border border-[#1B4D3E]/10 hover:border-[#D4AF37]/50 hover:shadow-xl transition-all group"
              data-testid={`feature-${index}`}
            >
              <div className="w-14 h-14 rounded-2xl bg-[#1B4D3E]/5 flex items-center justify-center mb-6 group-hover:bg-[#1B4D3E] transition-colors">
                <feature.icon className="w-7 h-7 text-[#1B4D3E] group-hover:text-white transition-colors" strokeWidth={1.5} />
              </div>
              <h3 className="font-display text-2xl text-[#1A1A1A] mb-3">{feature.title}</h3>
              <p className="text-[#4A4A4A] font-light leading-relaxed">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>

        <div className="text-center mt-12">
          <Link to="/services" className="btn-secondary">
            Explore All Features
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Industries Section
const industries = [
  { icon: Stethoscope, name: "Medical Practices", description: "Primary care, specialists, and multi-provider clinics" },
  { icon: HeartPulse, name: "Mental Health", description: "Therapists, counselors, and psychiatric practices" },
  { icon: Building2, name: "Health Systems", description: "Hospitals, urgent care, and healthcare networks" },
  { icon: Users, name: "Wellness Centers", description: "Physical therapy, chiropractic, and wellness clinics" },
];

const IndustriesSection = () => (
  <section className="py-32 md:py-40 bg-white" data-testid="industries-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
        <AnimatedSection>
          <div className="flex items-center gap-3 mb-6">
            <div className="gold-divider" />
            <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">Who We Serve</span>
          </div>
          <h2 className="font-display text-4xl md:text-5xl text-[#1A1A1A] mb-6">
            Built for
            <br />
            <span className="italic text-[#1B4D3E]">Healthcare</span>
          </h2>
          <p className="text-lg text-[#4A4A4A] font-light leading-relaxed mb-8">
            Whether you're a solo practitioner or a large health system, our platform scales to meet your needs 
            with industry-specific features and workflows.
          </p>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-6">
            {industries.map((industry, index) => (
              <div key={industry.name} className="flex gap-4" data-testid={`industry-${index}`}>
                <div className="flex-shrink-0 w-12 h-12 rounded-xl bg-[#1B4D3E]/5 flex items-center justify-center">
                  <industry.icon className="w-6 h-6 text-[#1B4D3E]" />
                </div>
                <div>
                  <h4 className="font-semibold text-[#1A1A1A] mb-1">{industry.name}</h4>
                  <p className="text-sm text-[#4A4A4A]">{industry.description}</p>
                </div>
              </div>
            ))}
          </div>
        </AnimatedSection>

        <AnimatedSection delay={0.2}>
          <img 
            src="https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800"
            alt="Healthcare professionals"
            className="rounded-3xl shadow-2xl"
          />
        </AnimatedSection>
      </div>
    </div>
  </section>
);

// How It Works Section
const ProcessSection = () => {
  const steps = [
    { number: "01", title: "Schedule a Demo", description: "See the platform in action with a personalized walkthrough from our team." },
    { number: "02", title: "Easy Onboarding", description: "We migrate your data and train your staff—typically live within 2 weeks." },
    { number: "03", title: "Grow Your Practice", description: "Focus on patient care while we handle the technology." }
  ];

  return (
    <section id="process" className="dark-section py-32 md:py-40" data-testid="process-section">
      <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-12">
        <AnimatedSection className="text-center mb-20">
          <div className="flex items-center justify-center gap-3 mb-6">
            <div className="w-12 h-px bg-[#D4AF37]" />
            <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#D4AF37]/80">How It Works</span>
            <div className="w-12 h-px bg-[#D4AF37]" />
          </div>
          <h2 className="font-display text-4xl md:text-6xl text-white mb-6">Get Started in Days</h2>
          <p className="text-lg text-white/60 font-light max-w-2xl mx-auto">
            Simple implementation with dedicated support every step of the way.
          </p>
        </AnimatedSection>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8 md:gap-16">
          {steps.map((step, index) => (
            <AnimatedSection key={step.number} delay={index * 0.15}>
              <div className="process-step" data-testid={`process-step-${index}`}>
                {index < steps.length - 1 && <div className="process-line hidden md:block" />}
                <div className="process-step-number">{step.number}</div>
                <h3 className="font-display text-2xl text-white mb-4">{step.title}</h3>
                <p className="text-white/60 font-light leading-relaxed">{step.description}</p>
              </div>
            </AnimatedSection>
          ))}
        </div>

        <div className="text-center mt-16">
          <Link to="/book" className="btn-gold" data-testid="process-cta">
            Request Your Demo
            <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </div>
    </section>
  );
};

// Testimonials Section
const testimonials = [
  { quote: "NeoBody transformed how we manage our practice. Scheduling alone saves us 10+ hours per week.", author: "Dr. Sarah M.", title: "Family Medicine Practice", rating: 5 },
  { quote: "The best investment we've made. Patient satisfaction is up 40% since we implemented NeoBody.", author: "Michael R.", title: "Multi-Location Clinic", rating: 5 },
  { quote: "Finally, software that understands healthcare. HIPAA compliant and actually easy to use.", author: "Jennifer K.", title: "Mental Health Center", rating: 5 }
];

const TestimonialsSection = () => (
  <section className="py-32 md:py-40 bg-[#F9F9F7]" data-testid="testimonials-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <AnimatedSection className="mb-20">
        <div className="flex items-center gap-3 mb-6">
          <div className="gold-divider" />
          <span className="text-sm font-medium tracking-[0.2em] uppercase text-[#888]">Customer Stories</span>
        </div>
        <h2 className="font-display text-4xl md:text-6xl text-[#1A1A1A]">Trusted by Healthcare Leaders</h2>
      </AnimatedSection>

      <motion.div
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={{ once: true }}
        className="grid grid-cols-1 md:grid-cols-3 gap-8"
      >
        {testimonials.map((testimonial, index) => (
          <motion.div key={index} variants={fadeUp} className="testimonial-card" data-testid={`testimonial-${index}`}>
            <div className="flex gap-1 mb-6">
              {[...Array(testimonial.rating)].map((_, i) => (
                <Star key={i} className="w-4 h-4 fill-[#D4AF37] text-[#D4AF37]" />
              ))}
            </div>
            <p className="testimonial-quote mb-8">"{testimonial.quote}"</p>
            <div>
              <p className="font-medium text-[#1A1A1A]">{testimonial.author}</p>
              <p className="text-sm text-[#888]">{testimonial.title}</p>
            </div>
          </motion.div>
        ))}
      </motion.div>
    </div>
  </section>
);

// CTA Section
const CTASection = () => (
  <section className="py-32 md:py-40 bg-white" data-testid="cta-section">
    <div className="max-w-[1400px] mx-auto px-6 md:px-12">
      <div className="bg-[#1B4D3E] rounded-3xl p-12 md:p-20 text-center relative overflow-hidden">
        <div className="absolute inset-0 opacity-20">
          <div className="absolute top-0 left-1/4 w-96 h-96 bg-[#D4AF37] rounded-full blur-3xl" />
          <div className="absolute bottom-0 right-1/4 w-96 h-96 bg-white rounded-full blur-3xl" />
        </div>
        <div className="relative z-10">
          <h2 className="font-display text-4xl md:text-5xl text-white mb-6">Ready to Transform Your Practice?</h2>
          <p className="text-lg text-white/70 font-light max-w-xl mx-auto mb-10">
            Join 500+ healthcare organizations already using NeoBody to streamline operations and improve patient care.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/book" className="btn-gold">
              Request Demo
              <ArrowRight className="w-4 h-4" />
            </Link>
            <Link to="/contact" className="btn-secondary !border-white/30 !text-white hover:!bg-white/10">
              Contact Sales
            </Link>
          </div>
        </div>
      </div>
    </div>
  </section>
);

// Main Home Page
const HomePage = () => {
  return (
    <div data-testid="home-page">
      <HeroSection />
      <FeaturesSection />
      <IndustriesSection />
      <ProcessSection />
      <TestimonialsSection />
      <CTASection />
    </div>
  );
};

export default HomePage;
